import { ImagePaths } from '../../lib/imageUrls.js';

export const hybridIdentityWorkshop = {

    id: 34,
    slug: 'hybrid-identity-workshop',
    ogImage: '/og/courses/hybrid-identity-workshop.jpg',
    courseCode: 'HYB-ID-WS-001',
    featured: false,
    title: 'Hybrid Identity Workshop — AD to Entra cutover for Australian organisations',
    subtitle: 'Plan and execute a safe hybrid Active Directory to Microsoft Entra ID transition',
    valueProposition: 'A two-day intensive that walks identity architects and Active Directory administrators through every decision, control and rollback step required to move from hybrid identity to cloud-only Microsoft Entra ID without breaking authentication for Australian workforces.',
    duration: '16 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_2,
    shortDescription: 'HYB-ID-WS-001 — hybrid identity workshop covering AD to Entra ID cutover, coexistence patterns and federation decommissioning for identity architects.',
    price: {
      aud: 999,
      currency: 'AUD',
      earlyBird: 799
    },
    stripeConfig: {
      productId: 'prod_UarsJyfT8BnwlW',
      priceId: 'price_1TikaJKu1nyc322U4dduZ5yA'
    },

    snapshot: {
      whoItsFor: [
        'Identity and access architects designing the target Entra ID state',
        'Active Directory administrators responsible for Entra Connect and AD FS',
        'Microsoft 365 engineers running coexistence and device join programmes',
        'Security and risk leads accountable for the cutover risk register'
      ],
      accreditation: 'Aligned to Microsoft Learn hybrid identity reference architecture and ACSC Information Security Manual identity controls'
    },

    logistics: {
      classSize: 'Self-paced online with live Q&A sessions',
      requirements: [
        'Working knowledge of Active Directory Domain Services and Group Policy',
        'Familiarity with Microsoft Entra ID (formerly Azure AD) tenants and Conditional Access',
        'Access to a non-production tenant and lab AD forest for the hands-on labs',
        'Global Administrator and Domain Admin rights in the lab environment'
      ]
    },

    overview: 'Microsoft retired Azure AD Connect v1 on 31 August 2022 and is steadily moving customers from federation and on-premises sync towards cloud-only identity. This workshop helps Australian organisations sequence that journey safely. Across two intensive days you will compare Entra Connect Sync, Entra Cloud Sync and federation topologies, design coexistence patterns for devices and groups, and rehearse a federation-to-cloud-authentication cutover with a documented rollback path. The course is grounded in Microsoft Learn reference guidance and the identity expectations in the ACSC Information Security Manual and ASD Essential Eight maturity model.\n\nYou will leave with a tailored cutover plan, a risk register template, a decommissioning checklist for AD FS and unused domain controllers, and the post-cutover hygiene routines required to keep hybrid joined devices, group writeback and single sign-on healthy.',

    outcomes: [
      'Select the right hybrid identity topology (Entra Connect Sync, Entra Cloud Sync or federation) for your tenant size, geography and compliance posture',
      'Design coexistence patterns covering group writeback, hybrid Entra joined devices and seamless single sign-on for Australian workforces',
      'Migrate authentication from AD FS federation to cloud authentication using password hash sync or pass-through authentication with staged rollout',
      'Build a cutover risk register that maps technical, regulatory and user-experience risks to mitigations and rollback triggers',
      'Execute a rehearsed rollback to federation or to a previous sync server within agreed recovery time objectives',
      'Decommission AD FS farms, Web Application Proxy and redundant domain controllers without leaving orphaned trust or certificate dependencies',
      'Apply post-cutover hygiene controls aligned to ASD Essential Eight and ACSC ISM identity guidance',
      'Communicate the cutover plan and residual risks to executives, auditors and the OAIC-aligned privacy function'
    ],

    audience: [
      'Identity and access architects designing the target Microsoft Entra ID state',
      'Active Directory administrators responsible for Entra Connect Sync and AD FS',
      'Microsoft 365 engineers running device join, group writeback and coexistence programmes',
      'Security and risk leads accountable for the cutover risk register and rollback decision',
      'Infrastructure managers planning domain controller and AD FS farm decommissioning',
      'Consultants sequencing hybrid-to-cloud identity migrations for Australian clients'
    ],

    prerequisites: [
      'Working knowledge of Active Directory Domain Services, Group Policy and DNS',
      'An established hybrid identity environment using Entra Connect Sync or AD FS',
      'Familiarity with Microsoft Entra ID tenants and Conditional Access policy',
      'Access to a non-production tenant and lab AD forest for the hands-on exercises',
      'Global Administrator and Domain Admin rights in the lab environment'
    ],

    modules: [
      {
        number: 1,
        title: 'Hybrid identity topology choices',
        duration: '3 hours',
        topics: [
          'Comparing Entra Connect Sync, Entra Cloud Sync and federation',
          'Microsoft Entra Connect v2 requirements and the v1 retirement notice',
          'Sizing, high availability and Australian data residency considerations',
          'Choosing password hash sync, pass-through authentication or federation',
          'Mapping topology choices to ACSC ISM identity controls'
        ]
      },
      {
        number: 2,
        title: 'Coexistence patterns',
        duration: '3.5 hours',
        topics: [
          'Group writeback v2 and lifecycle management for Microsoft 365 groups',
          'Hybrid Entra joined devices and Windows Hello for Business rollout',
          'Seamless single sign-on, Kerberos and primary refresh tokens',
          'Application coexistence: Kerberos, header-based and SAML apps',
          'Conditional Access design for mixed device estates'
        ]
      },
      {
        number: 3,
        title: 'Migrating from federation to cloud authentication',
        duration: '3.5 hours',
        topics: [
          'Inventorying AD FS relying parties and custom claim rules',
          'Staged rollout to password hash sync or pass-through authentication',
          'Handling legacy authentication, smart cards and certificate-based sign-in',
          'Cutting over Microsoft 365, Azure resources and third-party SaaS',
          'Validating MFA, Conditional Access and break-glass accounts post-cutover'
        ]
      },
      {
        number: 4,
        title: 'Cutover risk register and rollback strategies',
        duration: '3 hours',
        topics: [
          'Building a risk register covering authentication, devices and apps',
          'Defining rollback triggers, owners and recovery time objectives',
          'Reverting from cloud authentication to federation safely',
          'Communications plan for users, service desk and executives',
          'Privacy Act 1988 and OAIC notifiable data breach considerations'
        ]
      },
      {
        number: 5,
        title: 'Post-cutover hygiene',
        duration: '3 hours',
        topics: [
          'Decommissioning AD FS, Web Application Proxy and dbConfig backups',
          'Retiring redundant domain controllers and cleaning DNS, SCP and SPNs',
          'Tightening Entra Connect Sync rules and on-premises attribute flows',
          'Ongoing monitoring with Entra ID sign-in logs and Microsoft Sentinel',
          'Maturity uplift against ASD Essential Eight Restrict Administrative Privileges and MFA'
        ]
      }
    ],

    format: {
      type: 'Two-day live online workshop with live Q&A',
      includes: [
        '16 hours of workshop instruction across five modules',
        'Hands-on labs in your own non-production tenant and lab AD forest',
        'A tailored cutover plan you build during the workshop, not a generic template',
        'Cutover risk register template mapping technical, regulatory and user-experience risks',
        'Federation rollback runbook with defined triggers, owners and recovery time objectives',
        'Decommissioning checklist for AD FS, Web Application Proxy and redundant domain controllers',
        'Post-cutover hygiene routines for hybrid joined devices, group writeback and seamless SSO',
        'Certificate of completion'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Two-day cohorts run every six weeks. Private cohorts suit identity squads attending together — architects, AD administrators and security leads in the same room'
    },

    certification: 'Educ4te Hybrid Identity Workshop Certificate of Completion. Counts towards Continuing Professional Development hours with ACS, ISACA and (ISC)2.',

    faqs: [
      {
        question: 'Does this workshop assume we are already running Microsoft Entra Connect?',
        answer: 'Yes. We assume you have an established hybrid identity environment using Entra Connect Sync or AD FS. The workshop focuses on planning and executing the transition towards cloud-only identity rather than the initial hybrid setup.'
      },
      {
        question: 'How does the course address the retirement of Azure AD Connect v1?',
        answer: 'Microsoft retired Azure AD Connect v1 on 31 August 2022. We walk through the upgrade path to Microsoft Entra Connect v2, the supported coexistence window, and how to validate a clean migration before planning the broader federation cutover.'
      },
      {
        question: 'Will we cover Microsoft Entra Cloud Sync as an alternative to Entra Connect Sync?',
        answer: 'Yes. Module 1 compares the agent-based Entra Cloud Sync model with the classic Entra Connect Sync server, including filtering, attribute flow differences and which workloads each topology supports today per Microsoft Learn guidance.'
      },
      {
        question: 'Is the content aligned to Australian compliance frameworks?',
        answer: 'The workshop maps each design decision to the ACSC Information Security Manual identity controls and the ASD Essential Eight maturity model, with privacy considerations referenced against the OAIC Australian Privacy Principles and Privacy Act 1988.'
      },
      {
        question: 'Do participants get a rollback template?',
        answer: 'Yes. You receive a cutover risk register, a federation rollback runbook and a decommissioning checklist for AD FS and domain controllers. These are designed to be tailored to your tenant and lodged with your change advisory board.'
      },
      {
        question: 'Can teams attend together?',
        answer: 'Yes. The two-day format works well for identity squads. Contact us for cohort pricing if you want to run a private session for your architects, AD administrators and security leads together.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['ASD Essential Eight', 'ACSC ISM', 'OAIC APPs']
};
